/**
 * Territories out of the page: a file, or sheets of paper.
 *
 * Both carry the same two things - the division, and what is inside it - so
 * both are built from one list of TerritorySheet: the file as a row per lead
 * with its territory's columns beside it, the printout as a sheet per
 * territory with its leads in a table. Nothing here fetches; the page reads
 * the leads and hands them over, which is what lets these be tested as text.
 *
 * The map is not printed. A boundary is the one thing about a territory that
 * paper does not hold usefully, and the page is where it is drawn.
 */
import type { WorkItem } from '../board/leads-board.component';
import type { TerritoryWithMetrics } from '../../../map/features/territories/territory-feature';
import { territoryPaletteColor } from '../../../map/features/territories/territory-colors';
import { parseLocalDateString } from '../../../shared/date.util';

/** Export writes a file; Print opens the browser's print dialog. */
export type SheetAction = 'export' | 'print';

/**
 * Which territories. `selected` is the open patch, `shown` is what Mine and
 * the filter rules leave on the roster, `all` is every one the page holds.
 */
export type SheetScope = 'selected' | 'shown' | 'all';

/** A territory and every lead in it, in the order they are to be written. */
export interface TerritorySheet {
  row: TerritoryWithMetrics;
  leads: readonly WorkItem[];
}

/** A user id as the page prints it. */
export type RepName = (userId: number) => string;

type Cell = string | number | null | undefined;

/**
 * A territory drawn on the page and not saved yet. The server has no id for
 * it, so it has no leads to list - which is not the same as having none.
 */
function unsaved(row: TerritoryWithMetrics): boolean {
  return row.territory.id < 0;
}

// ── The file ───────────────────────────────────────────────────────────────

/**
 * The territory's columns, first on every row.
 *
 * TerritoryLeads is the number of rows that follow for it, so the file's own
 * arithmetic always agrees with itself. Blank for a territory that is not
 * saved, which has no count yet rather than a count of none.
 */
const TERRITORY_COLUMNS: readonly (readonly [string, (sheet: TerritorySheet, rep: RepName) => Cell])[] = [
  ['TerritoryId', ({ row }) => (unsaved(row) ? null : row.territory.id)],
  ['Territory', ({ row }) => row.territory.name],
  ['TerritoryStatus', ({ row }) => row.territory.status],
  ['TerritoryRep', ({ row }, rep) =>
    (row.territory.assignedUserId == null ? 'Unassigned' : rep(row.territory.assignedUserId))],
  ['TerritoryLeads', ({ row, leads }) => (unsaved(row) ? null : leads.length)],
  // Null when the viewer may not see customers: blank, never a nought that
  // would say the territory has none.
  ['TerritoryCustomers', ({ row }) => row.customers],
  ['TerritoryOpenPipeline', ({ row }) => row.openPipelineValue],
  ['TerritoryAreaSqKm', ({ row }) => Math.round(row.areaSqKm * 100) / 100],
];

/**
 * The lead's columns, named as the leads export names the same fields. The
 * rep is a name here rather than the user id that file carries: this file is
 * for reading, not for bringing back.
 */
const LEAD_COLUMNS: readonly (readonly [string, (lead: WorkItem, rep: RepName) => Cell])[] = [
  ['LeadId', lead => lead.leadId],
  ['Name', lead => lead.title ?? `Lead #${lead.leadId}`],
  ['AddressLine1', lead => lead.address],
  ['City', lead => lead.city],
  ['State', lead => lead.state],
  ['PostalCode', lead => lead.postalCode],
  ['Stage', lead => lead.boardColumn],
  ['SalesRep', (lead, rep) => (lead.salesRepUserId == null ? null : rep(lead.salesRepUserId))],
  ['NextActionType', lead => lead.nextActionType],
  ['NextActionDue', lead => lead.nextActionDue],
  ['ExpectedAnnualContribution', lead => lead.contributionBase],
  // As the page shows it (25%), not the raw 0.25.
  ['Probability', lead => (lead.probability == null ? null : `${Math.round(lead.probability * 100)}%`)],
  ['Latitude', lead => lead.lat],
  ['Longitude', lead => lead.lng],
];

/**
 * One cell. Text that opens with = + - or @ is run as a formula by a
 * spreadsheet, and names and addresses here came from somebody's import, so
 * that text is prefixed with a quote. Numbers are written as they are: a
 * longitude of -74.5 guarded the same way would open as text, not a number.
 */
function csvCell(value: Cell): string {
  if (value == null) return '';
  if (typeof value === 'number') return String(value);
  const text = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * The file: a header, then a row per lead with its territory's columns beside
 * it, territory by territory in the order given.
 *
 * A territory with no leads is still one of the divisions, so it keeps a row
 * of its own with the lead columns empty. Leaving it out would make a file of
 * "all territories" that quietly lists fewer.
 */
export function territorySheetsCsv(sheets: readonly TerritorySheet[], rep: RepName): string {
  const header = [...TERRITORY_COLUMNS, ...LEAD_COLUMNS].map(([name]) => name).join(',');
  const noLead = LEAD_COLUMNS.map(() => '');
  const lines = sheets.flatMap(sheet => {
    const territory = TERRITORY_COLUMNS.map(([, cell]) => csvCell(cell(sheet, rep)));
    return sheet.leads.length
      ? sheet.leads.map(lead =>
          [...territory, ...LEAD_COLUMNS.map(([, cell]) => csvCell(cell(lead, rep)))].join(','))
      : [[...territory, ...noLead].join(',')];
  });
  // BOM so Excel on Windows reads UTF-8 - territory names are not all ASCII -
  // and CRLF per RFC 4180: the same two choices the leads export makes.
  return '\uFEFF' + [header, ...lines].join('\r\n');
}

/**
 * `territories-2026-09-10.csv`, or `territory-north-2026-09-10.csv` when the
 * file is one territory. Letters in any script are kept, so a territory named
 * in Chinese keeps its name; a name with none to keep falls back to the id.
 */
export function territorySheetsFilename(sheets: readonly TerritorySheet[], day: string): string {
  if (sheets.length !== 1) return `territories-${day}.csv`;
  const { territory } = sheets[0].row;
  const slug = territory.name.toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '');
  return `territory-${slug || territory.id}-${day}.csv`;
}

// ── The printout ───────────────────────────────────────────────────────────

export interface SheetPrintOptions {
  rep: RepName;
  /** Which territories, in the words the menu used: "All 12 territories". */
  scope: string;
  printedAt: Date;
  /**
   * Whether the page holds staged edits. The lead lists are read from the
   * saved map, so a printout made over unsaved changes says so rather than
   * passing the saved lists off as the edited ones.
   */
  unsavedChanges: boolean;
}

const money = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
});
const tenths = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

function esc(value: string | number): string {
  return String(value).replace(/[&<>"']/g, ch => `&#${ch.charCodeAt(0)};`);
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** "Sep 12", as the page's own table shows a next action. A date-only value
 *  is a calendar day and is read as one, not as midnight in UTC. */
function shortDay(value: string): string {
  const day = parseLocalDateString(value) ?? new Date(value);
  return Number.isNaN(day.getTime())
    ? value
    : day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function leadRow(lead: WorkItem, index: number, rep: RepName): string {
  const place = [[lead.city, lead.state].filter(Boolean).join(', '), lead.postalCode]
    .filter(Boolean).join(' ');
  const where = lead.address
    ? esc(lead.address) + (place ? `<div class="sub">${esc(place)}</div>` : '')
    : (place ? esc(place) : '—');
  const next = lead.nextActionType
    ? esc(lead.nextActionType) + (lead.nextActionDue ? ` · ${esc(shortDay(lead.nextActionDue))}` : '')
    : '—';
  return '<tr>'
    + `<td class="n">${index + 1}</td>`
    + `<td>${esc(lead.title ?? `Lead #${lead.leadId}`)}</td>`
    + `<td>${where}</td>`
    + `<td>${esc(lead.boardColumn ?? '')}</td>`
    + `<td>${lead.salesRepUserId == null ? '—' : esc(rep(lead.salesRepUserId))}</td>`
    + `<td>${next}</td>`
    + '</tr>';
}

function sheetHtml({ row, leads }: TerritorySheet, rep: RepName): string {
  const { territory } = row;
  const owner = territory.assignedUserId == null ? 'Unassigned' : rep(territory.assignedUserId);
  const facts = [
    unsaved(row) ? null : plural(leads.length, 'lead', 'leads'),
    row.customers == null ? null : plural(row.customers, 'customer', 'customers'),
    `${tenths.format(row.areaSqKm)} km²`,
    `${money.format(row.openPipelineValue)} open pipeline`,
  ].filter((fact): fact is string => fact !== null).join(' · ');

  const body = unsaved(row)
    ? '<p class="empty">Not saved yet. Its leads are listed once the map is saved.</p>'
    : leads.length
      ? '<table><thead><tr><th class="n">#</th><th>Lead</th><th>Address</th>'
        + '<th>Stage</th><th>Sales rep</th><th>Next action</th></tr></thead>'
        + `<tbody>${leads.map((lead, i) => leadRow(lead, i, rep)).join('')}</tbody></table>`
      : '<p class="empty">No leads in this territory.</p>';

  // The palette's colour, never the stored string: it lands in a style attribute.
  return '<section class="sheet">'
    + `<h2><span class="dot" style="background:${territoryPaletteColor(territory.color)}"></span>`
    + `${esc(territory.name)}</h2>`
    + `<p class="facts"><b>${esc(territory.status)}</b> · Rep: ${esc(owner)}</p>`
    + `<p class="facts">${esc(facts)}</p>`
    + body
    + '</section>';
}

/**
 * One territory to a page: the first sheet follows the heading, every later
 * one starts a page of its own, and a long table repeats its column heads
 * when it runs onto the next.
 */
const PRINT_CSS = `
@page { margin: 14mm 12mm; }
* { box-sizing: border-box; }
body { margin: 0; color: #1f2933;
  font: 10pt/1.35 system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif; }
.doc { margin-bottom: 6mm; padding-bottom: 3mm; border-bottom: 1px solid #cbd2d9; }
.doc h1 { margin: 0; font-size: 16pt; }
.doc p { margin: 1mm 0 0; color: #52606d; }
.doc .note { color: #8d2b0b; }
.sheet + .sheet { break-before: page; }
.sheet h2 { display: flex; align-items: center; gap: 2.5mm; margin: 0 0 1.5mm; font-size: 14pt; }
.dot { flex: none; width: 3.5mm; height: 3.5mm; border-radius: 50%;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.facts { margin: 0 0 1mm; color: #52606d; }
.facts b { color: #1f2933; }
table { width: 100%; margin-top: 3mm; border-collapse: collapse; }
thead { display: table-header-group; }
tr { break-inside: avoid; }
th { padding: 1.5mm 2mm; border-bottom: 1.5px solid #9aa5b1; color: #52606d;
  font-size: 8.5pt; font-weight: 600; text-align: left; text-transform: uppercase; letter-spacing: .03em; }
td { padding: 1.5mm 2mm; border-bottom: 1px solid #e4e7eb; vertical-align: top; }
.n { width: 8mm; color: #7b8794; text-align: right; }
.sub { color: #7b8794; }
.empty { margin-top: 4mm; color: #7b8794; font-style: italic; }
`;

/** The printout, as a document of its own for the browser to print. */
export function territorySheetsHtml(
  sheets: readonly TerritorySheet[], options: SheetPrintOptions,
): string {
  const day = options.printedAt.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
  const note = options.unsavedChanges
    ? '<p class="note">This page has unsaved changes. The lead lists below are the saved map\'s.</p>'
    : '';
  // The title is what the browser offers as the file name when the printout
  // is saved as a PDF.
  return '<!doctype html><html lang="en"><head><meta charset="utf-8">'
    + `<title>${esc(`Territories · ${options.scope} · ${day}`)}</title>`
    + `<style>${PRINT_CSS}</style></head><body>`
    + '<header class="doc"><h1>Territories</h1>'
    + `<p>${esc(options.scope)} · Printed ${esc(day)}</p>${note}</header>`
    + sheets.map(sheet => sheetHtml(sheet, options.rep)).join('')
    + '</body></html>';
}
